import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  PagePath,
  type ActivateReceiptPostingRequest,
  type ReceiptPostingActivationResult,
  type ReceiptPostingPreview,
  type VenueReceiptPostingState,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import { openVenueSetup } from '../venue-preparation-ui';
import {
  readPostingAttempt,
  savePostingAttempt,
  clearPostingAttempt,
} from './venue-receipt-posting-recovery';

class PostingPreflightError extends Error {}

export default function VenueReceiptPostingPanel({
  configuration,
  venueName,
  onBusyChange,
}: {
  configuration: VenueReceiptPostingState;
  venueName: string;
  onBusyChange: (busy: boolean) => void;
}) {
  const { t } = useTranslation(['admin', 'tournament']);
  const client = useQueryClient();
  const authenticated = useAuthStore((s) => s.isAuthenticated);
  const session = useAuthStore((s) => s.sessionRevision);
  const actor = useAuthStore((s) => s.user?.id);
  const target = configuration.targetVenueId;
  const [saved] = useState(() => readPostingAttempt(actor, target));
  const [preview, setPreview] = useState<ReceiptPostingPreview | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState(saved.request?.reason ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [operationKey] = useState(() => saved.request?.operationKey ?? crypto.randomUUID());
  const attempt = useRef<ActivateReceiptPostingRequest | null>(saved.request);
  const submitting = useRef(false);
  const submissionTag = useRef('');
  const [uncertain, setUncertain] = useState(!!saved.request);
  const [conflicted, setConflicted] = useState(saved.unreadable);
  const [page, setPage] = useState(0);
  const reasonInput = useRef<HTMLTextAreaElement>(null);
  const checkButton = useRef<HTMLButtonElement>(null);
  const activateButton = useRef<HTMLButtonElement>(null);
  const boundary = useRef(session);
  const assertSession = () => {
    if (
      !useAuthStore.getState().isAuthenticated ||
      useAuthStore.getState().sessionRevision !== boundary.current
    )
      throw new Error('Session changed');
  };
  const check = useMutation({
    retry: false,
    mutationFn: async () => {
      setError('');
      await openVenueSetup(target, PagePath.ADMIN_VENUES, client, () => undefined);
      assertSession();
      const data = await api.post<ReceiptPostingPreview>(
        `/venues/${target}/receipt-posting/preview`,
      );
      assertSession();
      if (data.targetVenueId !== target) throw new Error('Target mismatch');
      return data;
    },
    onSuccess: (data) => {
      if (data.state !== 'not-configured') {
        setExpanded(false);
        setPreview(null);
        void client.invalidateQueries({ queryKey: ['venue-preparation', target] });
        return;
      }
      setPreview(data);
      setPage(0);
      setExpanded(true);
      setConfirmed(false);
    },
    onError: () => setError(t('venue.posting.checkFailed')),
  });
  const activation = useMutation({
    retry: false,
    onMutate: () => {
      setError('');
      const tag = crypto.randomUUID();
      submissionTag.current = tag;
      return { tag };
    },
    mutationFn: async () => {
      assertSession();
      try {
        await openVenueSetup(target, PagePath.ADMIN_VENUES, client, () => undefined);
      } catch {
        throw new PostingPreflightError();
      }
      assertSession();
      if (!attempt.current) {
        if (!preview || preview.blockers.length || !confirmed || !reason.trim())
          throw new Error('Confirmation required');
        const request: ActivateReceiptPostingRequest = {
          operationKey,
          previewHash: preview.previewHash,
          reason: reason.trim(),
          confirmed: true,
        };
        savePostingAttempt(actor, target, request, false, submissionTag.current);
        attempt.current = request;
      } else savePostingAttempt(actor, target, attempt.current, true, submissionTag.current);
      const result = await api.post<ReceiptPostingActivationResult>(
        `/venues/${target}/receipt-posting/activate`,
        attempt.current,
        { retryAfterAuthRefresh: false },
      );
      assertSession();
      if (result.targetVenueId !== target || result.operationKey !== operationKey)
        throw new Error('Result identity mismatch');
      return result;
    },
    onSuccess: async (_result, _variables, context) => {
      clearPostingAttempt(actor, target, context?.tag);
      attempt.current = null;
      setUncertain(false);
      setPreview(null);
      setExpanded(false);
      setError('');
      await client.invalidateQueries({ queryKey: ['venue-preparation', target] });
      await client.invalidateQueries({ queryKey: ['entry-preview'] });
    },
    onError: (failure, _variables, context) => {
      if (failure instanceof PostingPreflightError) {
        setUncertain(!!attempt.current);
        setError(t('venue.posting.checkFailed'));
        return;
      }
      if (failure instanceof Error && failure.message === 'POSTING_RECOVERY_UNAVAILABLE') {
        setConflicted(true);
        setUncertain(false);
        setError(t('venue.posting.recoveryUnavailable'));
        return;
      }
      const response = axios.isAxiosError<{ code?: string; errorCode?: string }>(failure)
        ? failure.response
        : undefined;
      const code = response?.data?.errorCode ?? response?.data?.code;
      if (code && ['POSTING_PREVIEW_STALE', 'POSTING_NOT_READY'].includes(code)) {
        clearPostingAttempt(actor, target, context?.tag);
        attempt.current = null;
        setPreview(null);
        setConfirmed(false);
        setUncertain(false);
        setError(t('venue.posting.stale'));
      } else if (code === 'POSTING_OPERATION_CONFLICT' || response?.status === 400) {
        setConflicted(true);
        setExpanded(false);
        setError(t('venue.posting.conflict'));
      } else if (code === 'POSTING_ALREADY_CONFIGURED') {
        clearPostingAttempt(actor, target, context?.tag);
        attempt.current = null;
        setPreview(null);
        setExpanded(false);
        setUncertain(false);
        setConfirmed(false);
        setError(t('venue.posting.checkFailed'));
        void client.invalidateQueries({ queryKey: ['venue-preparation', target] });
      } else {
        setUncertain(!!attempt.current);
        setError(attempt.current ? '' : t('venue.posting.checkFailed'));
      }
    },
    onSettled: () => {
      submitting.current = false;
    },
  });
  const submit = () => {
    if (submitting.current) return;
    submitting.current = true;
    activation.mutate();
  };
  const pending = check.isPending || activation.isPending;
  useEffect(() => {
    if (pending) return;
    if (uncertain) activateButton.current?.focus();
    else if (expanded && preview) reasonInput.current?.focus();
  }, [pending, uncertain, expanded, preview]);
  useEffect(() => {
    onBusyChange(pending);
    return () => onBusyChange(false);
  }, [pending, onBusyChange]);
  const available =
    authenticated &&
    session === boundary.current &&
    !conflicted &&
    configuration.state === 'not-configured';
  const issues = preview ? [...preview.blockers, ...preview.warnings] : [];
  const pages = Math.max(1, Math.ceil(issues.length / 10));
  const evidenceSummary = (evidence: Record<string, number>) => {
    const counts: Record<string, number> = {};
    for (const [kind, count] of Object.entries(evidence)) {
      const group =
        kind === 'manual_counts'
          ? 'manual'
          : kind.includes('cash') || kind === 'custody'
            ? 'cash'
            : /charge|payment|quote|debt/.test(kind)
              ? 'receipts'
              : /timed|settlement/.test(kind)
                ? 'settlement'
                : /player|entry|entries|participation/.test(kind)
                  ? 'participation'
                  : 'related';
      counts[group] = (counts[group] ?? 0) + count;
    }
    return (
      Object.entries(counts)
        .map(([group, count]) => `${t(`venue.posting.evidence.${group}`)} ${count}`)
        .join(' · ') || '0'
    );
  };
  return (
    <section
      className="rounded border border-base-300 p-3 space-y-3"
      aria-label={t('venue.posting.title')}
    >
      <h4 className="font-semibold">{t('venue.posting.title')}</h4>
      <p>{t(`venue.posting.state.${configuration.state}`)}</p>
      <p className="text-sm">{t('venue.posting.support')}</p>
      {configuration.effectiveAt && (
        <p>
          {t('venue.posting.effectiveAt')}:{' '}
          <time dateTime={configuration.effectiveAt}>{configuration.effectiveAt}</time>
        </p>
      )}
      {configuration.approvalReference && (
        <p className="break-all text-sm">
          {t('venue.posting.reference')}: {configuration.approvalReference}
        </p>
      )}
      {configuration.state !== 'not-configured' && (
        <p className="text-sm">{t('venue.posting.readOnly')}</p>
      )}
      {pending && <p role="status">{t('venue.posting.pending')}</p>}
      {error && (
        <p role="alert" className="text-error">
          {error}
        </p>
      )}
      {saved.unreadable && <p role="alert">{t('venue.posting.recoveryUnavailable')}</p>}
      {uncertain && attempt.current && (
        <div className="space-y-2">
          <p role="alert">{t('venue.posting.retrySame')}</p>
          <p className="text-sm break-all">
            {t('venue.posting.target')}: {venueName} ({target})
          </p>
          <p className="text-sm whitespace-pre-wrap break-words">{attempt.current.reason}</p>
          <button
            ref={activateButton}
            type="button"
            className="min-h-11 rounded bg-primary px-4 py-2 text-primary-content disabled:opacity-50"
            disabled={pending || !authenticated || session !== boundary.current || conflicted}
            onClick={submit}
          >
            {t('venue.posting.retry')}
          </button>
        </div>
      )}
      {available && !uncertain && (
        <button
          type="button"
          ref={checkButton}
          className="min-h-11 rounded border border-base-300 px-4 py-2 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-primary"
          disabled={pending}
          onClick={() => check.mutate()}
        >
          {t('venue.posting.check')}
        </button>
      )}
      {expanded && preview && (
        <div className="space-y-3">
          <p className="break-all">
            {t('venue.posting.target')}: {venueName}
            <span className="block text-xs break-all">{target}</span>
          </p>
          <p className="text-sm">
            {t('venue.posting.checkedAt')}: <time dateTime={preview.asOf}>{preview.asOf}</time>
          </p>
          {issues.slice(page * 10, (page + 1) * 10).map((item, index) => (
            <p
              key={`${item.reasonCode}:${item.tournamentId}:${index}`}
              className={preview.blockers.includes(item) ? 'text-error' : ''}
            >
              {t(`venue.posting.reason.${item.reasonCode}`)}{' '}
              {item.tournamentId && (
                <span className="break-all text-sm">({item.tournamentId})</span>
              )}
              {item.status && (
                <span className="block text-sm">
                  {t(`tournament:status.${item.status}`, { defaultValue: item.status })}
                </span>
              )}
              <span className="block text-sm">
                {t('venue.posting.evidence')}: {evidenceSummary(item.evidence)}
              </span>
            </p>
          ))}
          {pages > 1 && (
            <nav
              className="flex flex-wrap items-center gap-2"
              aria-label={t('venue.posting.issues')}
            >
              <button
                type="button"
                className="min-h-11 rounded border border-base-300 px-3"
                disabled={pending || page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('venue.posting.previous')}
              </button>
              <span>{t('venue.posting.page', { page: page + 1, pages })}</span>
              <button
                type="button"
                className="min-h-11 rounded border border-base-300 px-3"
                disabled={pending || page + 1 >= pages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('venue.posting.next')}
              </button>
            </nav>
          )}
          {preview.blockers.length > 0 && <p>{t('venue.posting.reconcile')}</p>}
          {!preview.blockers.length && (available || uncertain) && (
            <>
              <label className="block">
                {t('venue.posting.reasonLabel')}
                <textarea
                  ref={reasonInput}
                  className="block min-h-24 w-full rounded border border-base-300 bg-base-100 p-2 focus-visible:outline-2 focus-visible:outline-primary"
                  maxLength={500}
                  value={reason}
                  disabled={pending || uncertain}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="h-5 w-5 shrink-0 accent-primary"
                  checked={confirmed}
                  disabled={pending || uncertain}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                {t('venue.posting.confirm')}
              </label>
              {!uncertain && (
                <button
                  ref={activateButton}
                  type="button"
                  className="mr-2 min-h-11 rounded bg-primary px-4 py-2 text-primary-content disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-primary"
                  disabled={pending || (!uncertain && (!confirmed || !reason.trim()))}
                  onClick={submit}
                >
                  {t('venue.posting.activate')}
                </button>
              )}
            </>
          )}
          <button
            type="button"
            className="min-h-11 rounded border border-base-300 px-4 py-2 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-primary"
            disabled={pending || uncertain}
            onClick={() => {
              setExpanded(false);
              setPreview(null);
              setConfirmed(false);
              checkButton.current?.focus();
            }}
          >
            {t('venue.posting.cancel')}
          </button>
        </div>
      )}
    </section>
  );
}
